import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ShoppingCartOutlined,
  AppstoreOutlined,
  SlidersOutlined,
  TagsOutlined,
  PercentageOutlined,
  GiftOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  AreaChartOutlined,
  WalletOutlined,
  ApiOutlined,
  HistoryOutlined,
  ShopOutlined,
  AuditOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import { usePermissions } from 'sama-shared';
import { SYSTEM_PERMISSIONS } from 'sama-shared';

export const useNavigationMenu = () => {
  const { pathname } = useLocation();
  const { hasPermission, isSuperadmin } = usePermissions();

  const localMenuItems = useMemo(
    () => [
      ...(hasPermission(SYSTEM_PERMISSIONS.ORDER_READ)
        ? [{ key: '/orders', icon: <ShoppingCartOutlined />, label: 'Pedidos' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.PRODUCT_READ)
        ? [{ key: '/productos', icon: <AppstoreOutlined />, label: 'Productos' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.MODIFIER_READ)
        ? [{ key: '/modificadores', icon: <SlidersOutlined />, label: 'Modificadores' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.CATEGORY_READ)
        ? [{ key: '/categories', icon: <TagsOutlined />, label: 'Categorías' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.OFFER_READ)
        ? [{ key: '/ofertas', icon: <PercentageOutlined />, label: 'Ofertas' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.PRODUCT_READ)
        ? [{ key: '/promos', icon: <GiftOutlined />, label: 'Promos' }]
        : []),
    ],
    [hasPermission]
  );

  const globalMenuItems = useMemo(
    () => [
      ...(hasPermission(SYSTEM_PERMISSIONS.LOCATION_READ)
        ? [{ key: '/locations', icon: <EnvironmentOutlined />, label: 'Sucursales' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.USER_READ)
        ? [{ key: '/colaboradores', icon: <TeamOutlined />, label: 'Equipo' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.ROLE_READ)
        ? [{ key: '/roles', icon: <SafetyCertificateOutlined />, label: 'Roles' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.PARTNER_CONFIG_READ)
        ? [{ key: '/settings', icon: <SettingOutlined />, label: 'Ajustes' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.REPORTS_READ)
        ? [{ key: '/analytics', icon: <AreaChartOutlined />, label: 'Métricas' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ)
        ? [
            { key: '/finanzas', icon: <WalletOutlined />, label: 'Finanzas' },
            { key: '/comisiones', icon: <AuditOutlined />, label: 'Comisiones' },
            { key: '/facturas', icon: <ReconciliationOutlined />, label: 'Facturas' },
          ]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.PARTNER_CONFIG_READ)
        ? [{ key: '/delivery-integration', icon: <ApiOutlined />, label: 'Integración de Entrega' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.ORDER_READ)
        ? [{ key: '/order/history', icon: <HistoryOutlined />, label: 'Histórico de Pedidos' }]
        : []),
      ...(hasPermission(SYSTEM_PERMISSIONS.PRODUCT_READ)
        ? [{ key: '/master-catalog', icon: <AppstoreOutlined />, label: 'Catálogo Maestro' }]
        : []),
    ],
    [hasPermission]
  );

  const showGlobalGroup = isSuperadmin || globalMenuItems.length > 0;

  const menuItems = useMemo(() => {
    return showGlobalGroup
      ? [
          {
            key: 'local-group',
            icon: <ShopOutlined style={{ color: 'var(--sama-accent)' }} />,
            label: 'Sede Actual',
            children: localMenuItems,
          },
          {
            key: 'global-group',
            icon: <SettingOutlined style={{ color: 'var(--sama-accent)' }} />,
            label: 'Configuración',
            children: globalMenuItems,
          },
        ]
      : localMenuItems;
  }, [showGlobalGroup, localMenuItems, globalMenuItems]);

  const { selectedKey, openKey } = useMemo(() => {
    let matchedKey = '';
    let matchedGroup = '';

    const checkItems = (items: any[], parentKey?: string) => {
      for (const item of items) {
        if (item.key && !item.children) {
          if (pathname === item.key || pathname.startsWith(`${item.key}/`)) {
            if (item.key.length > matchedKey.length) {
              matchedKey = item.key;
              if (parentKey) matchedGroup = parentKey;
            }
          }
        }
        if (item.children) checkItems(item.children, item.key);
      }
    };

    checkItems(menuItems);

    return {
      selectedKey: matchedKey ? [matchedKey] : [],
      openKey: matchedGroup ? [matchedGroup] : ['local-group', 'global-group'],
    };
  }, [pathname, menuItems]);

  return { menuItems, selectedKey, openKey };
};
